import React from 'react';
import { FormControlLabel, Grid, InputLabel, MenuItem, OutlinedInput, Select, Switch } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import DefaultUploadFiles from '../../../../components/default-upload-files';
import DefaultSelectClient from '../../../../components/default-select-client';
import { DateField, FieldError, FormSectionTitle } from '../../../../components/form-page-parts';
import { IPreviewPublicationRequest } from '../../../../model/sdp/official-daily/request/preview-publication-request';
import { ValidationErrors } from '../../../../utils/validation';
import { parseNumberInput } from '../../../../utils/format';
import { PublicationLookups } from '../context/official-diary-publication-reducer';
import { PUBLIC_ACCOUNT_CATEGORY_ID, PublicationField, isBiddingCategory } from './publication-rules';

type RequestChange = (changes: Partial<IPreviewPublicationRequest>) => void;

export interface IFormPreviewPublicationProps {
    request: IPreviewPublicationRequest;
    lookups: PublicationLookups;
    errors: ValidationErrors<PublicationField>;
    files: File[];
    onChange: RequestChange;
    onCategoryChange: (value: unknown) => void;
    onClientChange: (e: any) => void;
    setFilesUpload: (files: File[]) => void;
}

interface LookupOption {
    id: number;
    description: string;
}

function LookupSelect({ label, value, options, error, onChange }: { label: string; value?: number; options: LookupOption[]; error?: string; onChange: (value: unknown) => void }) {
    return (
        <React.Fragment>
            <InputLabel required>{label}</InputLabel>
            <Select fullWidth displayEmpty value={value ?? ''} error={!!error} onChange={(e) => onChange(e.target.value)}>
                <MenuItem value=""><em>Selecione</em></MenuItem>
                {options.map((option) => <MenuItem key={option.id} value={option.id}>{option.description}</MenuItem>)}
            </Select>
            <FieldError message={error} />
        </React.Fragment>
    );
}

function GeneralSection({ request, errors, onChange, onClientChange }: Pick<IFormPreviewPublicationProps, 'request' | 'errors' | 'onChange' | 'onClientChange'>) {
    return (
        <React.Fragment>
            <Grid item xs={12} md={8}>
                <DefaultSelectClient required={false} clientId={request.clientId} disabled={request.submissionPublicationId !== undefined} handleOnChange={onClientChange} />
                <FieldError message={errors.clientId} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
                <DateField label="Data da publicação" required value={request.publicationDateOn} maxDate={dayjs()} error={errors.publicationDateOn} onChange={(value) => onChange({ publicationDateOn: value ?? undefined })} />
            </Grid>
            <Grid item xs={12} sm={6} md={2} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <FormControlLabel control={<Switch checked={request.republication} onChange={(e) => onChange({ republication: e.target.checked })} />} label="Republicação" />
            </Grid>
        </React.Fragment>
    );
}

function ClassificationSection({ request, lookups, errors, onChange, onCategoryChange }: Pick<IFormPreviewPublicationProps, 'request' | 'lookups' | 'errors' | 'onChange' | 'onCategoryChange'>) {
    return (
        <React.Fragment>
            <Grid item xs={12} md={6}>
                <LookupSelect label="Categoria" value={request.categoryId} options={lookups.categories} error={errors.categoryId} onChange={onCategoryChange} />
            </Grid>
            <Grid item xs={12} md={6}>
                <LookupSelect label="Subcategoria" value={request.subcategoryId} options={lookups.subcategories} error={errors.subcategoryId} onChange={(value) => onChange({ subcategoryId: parseNumberInput(value) })} />
            </Grid>
            <Grid item xs={12} md={6}>
                <LookupSelect label="Órgão de publicação" value={request.publicAgencyId} options={lookups.publicAgencies} error={errors.publicAgencyId} onChange={(value) => onChange({ publicAgencyId: parseNumberInput(value) })} />
            </Grid>
            <Grid item xs={12} md={6}>
                <LookupSelect label="Tipo de documento" value={request.documentTypeId} options={lookups.documentTypes} error={errors.documentTypeId} onChange={(value) => onChange({ documentTypeId: parseNumberInput(value) })} />
            </Grid>
        </React.Fragment>
    );
}

// Número/ano base e dados da comissão (estes só para licitações)
function DocumentDetailsSection({ request, onChange }: Pick<IFormPreviewPublicationProps, 'request' | 'onChange'>) {
    const bidding = isBiddingCategory(request.categoryId);
    return (
        <React.Fragment>
            <Grid item xs={12} sm={4} md={2}>
                <InputLabel>{request.categoryId === PUBLIC_ACCOUNT_CATEGORY_ID ? 'Ano base' : 'Número'}</InputLabel>
                <OutlinedInput fullWidth value={request.number ?? ''} onChange={(e) => onChange({ number: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={8} md={4}>
                <InputLabel>Comissão de licitação</InputLabel>
                <OutlinedInput fullWidth disabled={!bidding} value={request.descriptionCommission ?? ''} onChange={(e) => onChange({ descriptionCommission: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <InputLabel>Responsável da comissão de licitação</InputLabel>
                <OutlinedInput fullWidth disabled={!bidding} value={request.descriptionPresidentCommission ?? ''} onChange={(e) => onChange({ descriptionPresidentCommission: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
                <DateField label="Data de abertura" disabled={!bidding} value={request.sanctionDateOn} onChange={(value) => onChange({ sanctionDateOn: value ?? undefined })} />
            </Grid>
        </React.Fragment>
    );
}

function ContentSection({ request, errors, onChange }: Pick<IFormPreviewPublicationProps, 'request' | 'errors' | 'onChange'>) {
    return (
        <React.Fragment>
            <Grid item xs={12}>
                <InputLabel required>Descrição</InputLabel>
                <OutlinedInput fullWidth multiline rows={5} error={!!errors.description} value={request.description ?? ''} onChange={(e) => onChange({ description: e.target.value })} />
                <FieldError message={errors.description} />
            </Grid>
            <Grid item xs={12}>
                <InputLabel>Observação</InputLabel>
                <OutlinedInput fullWidth multiline rows={3} value={request.notes ?? ''} onChange={(e) => onChange({ notes: e.target.value })} />
            </Grid>
        </React.Fragment>
    );
}

export default function FormPreviewPublication(props: IFormPreviewPublicationProps) {
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <FormSectionTitle title="Dados gerais" first />
            <Grid container spacing={2}>
                <GeneralSection {...props} />
                <ClassificationSection {...props} />
                <DocumentDetailsSection {...props} />
            </Grid>
            <FormSectionTitle title="Conteúdo" />
            <Grid container spacing={2}>
                <ContentSection {...props} />
                <Grid item xs={12}>
                    <DefaultUploadFiles props={{ setFilesUpload: props.setFilesUpload, files: props.files }} />
                </Grid>
            </Grid>
        </LocalizationProvider>
    );
}
